import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	ContentWrapper,
	KpiGridContainer,
	LoaderWrapper,
	Wrapper,
} from './EditData.styles';
import { AgGridReact } from '@ag-grid-community/react';
import ServerSideDataSource from './ServerSideDataSource';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { useAnalyzeEditDataContext } from 'contexts/SupplierDiversity/AnalyzeEditDataProvider';
import KpiList from '../components/KpiList/KpiList';
import { updateDandICertifications } from 'utils/apiRequests';
import { useToastMessageContext } from 'contexts/ToastMessageContext/ToastMessageProvider';
import { NotificationTypes } from 'components/Toast/constants';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { getCurrencySymbolFromOptions } from 'SupplierDiversity/utils';
import { SettingsContext } from 'contexts/SettingsContext';

const EditData = () => {
	const [isUpdating, setIsUpdating] = useState(false);
	const { columnDefs } = useAnalyzeEditDataContext();
	const { showToast } = useToastMessageContext();
	const { dateFrom, dateTo } = useContext(QueryStringContext);
	const {
		options: { currency: currencyList },
	} = useContext(SettingsOptionsContext);
	const { currency } = useContext(QueryStringContext);
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
	const { hideDecimals } = useContext(SettingsContext);

	const dateRange = useMemo(
		() => ({
			dateFrom: dateFrom.split('T')[0],
			dateTo: dateTo.split('T')[0],
		}),
		[dateFrom, dateTo]
	);

	const [gridOptions, setGridOptions] = useState({
		modules: AllModules,
		columnDefs: columnDefs,
		cacheBlockSize: 200,
		maxBlocksInCache: 10,
		enableColResize: true,
		enableSorting: true,
		enableFilter: true,
		rowModelType: 'serverSide',
		floatingFilter: true,
		maxConcurrentDatasourceRequests: 1,
		multiSortKey: 'ctrl',
		enableRangeSelection: true,
		suppressCutToClipboard: true,
	});

	const defaultColDefs = useMemo(() => {
		return {
			flex: 1,
			minWidth: 100,
			editable: false,
		};
	}, []);
	const gridRef = useRef();

	useEffect(
		() => {
			setGridOptions(prevState => ({
				...prevState,
				columnDefs: columnDefs,
			}));
		},
		[columnDefs]
	);

	useEffect(
		() => {
			const gridOptionsnew = {
				columnDefs,
				suppressRowClickSelection: true,
				...gridOptions,
			};
			const datasource = ServerSideDataSource(
				gridOptionsnew,
				gridRef.current.columnApi,
				gridRef.current.api,
				dateRange
			);
			gridRef.current.api.setServerSideDatasource(datasource);
		}, // eslint-disable-next-line
		[dateRange]
	);

	const onGridReady = useCallback(
		params => {
			gridRef.current.api.startEditingCell(params);
			const gridOptionsnew = {
				columnDefs,
				suppressRowClickSelection: true,
				...gridOptions,
			};
			const datasource = ServerSideDataSource(
				gridOptionsnew,
				params.columnApi,
				params.api,
				dateRange
			);
			params.api.setServerSideDatasource(datasource);
		},
		[columnDefs, gridOptions, dateRange]
	);

	const handleCellValueChange = e => {
		if (e.oldValue === e.newValue) {
			return;
		} else {
			setIsUpdating(true);
			const bodyParam = {
				VendorName: e.data.vENDOR_NAME,
				Certifications: e.newValue,
			};
			updateDandICertifications(bodyParam).then(response => {
				if (response.succeeded) {
					setIsUpdating(false);
					showToast(
						'Certification updated successfully.',
						NotificationTypes.success
					);
				} else {
					setIsUpdating(false);
					showToast(
						'Error occured while updating. Please try again.',
						NotificationTypes.danger,
						500
					);
				}
			});
		}
	};

	return (
		<Wrapper>
			<KpiGridContainer>
				<KpiList currencySymbol={currencySymbol} hideDecimals={hideDecimals} />
			</KpiGridContainer>
			{isUpdating ? (
				<LoaderWrapper>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoaderWrapper>
			) : (
				<ContentWrapper>
					<div className="ag-theme-balham">
						<AgGridReact
							ref={gridRef}
							modules={AllModules}
							columnDefs={columnDefs}
							cacheBlockSize={200}
							defaultColDef={defaultColDefs}
							maxBlocksInCache={10}
							enableColResize={true}
							enableSorting={true}
							enableFilter={true}
							enableGroupEdit={true}
							rowModelType="serverSide"
							maxConcurrentDatasourceRequests={1}
							multiSortKey="ctrl"
							onGridReady={onGridReady}
							enableRangeSelection={true}
							suppressCutToClipboard={true}
							suppressCellFocus={true}
							onCellValueChanged={handleCellValueChange}
						/>
					</div>
				</ContentWrapper>
			)}
		</Wrapper>
	);
};

export default EditData;
